/**
 * branchDataService — sube verisi icin SQL-only adaptor (Faz 4 sonrasi).
 *
 * OKUMA: `getBranches` yalniz SQL (`GET /api/Branch`, companyId X-Company-Id header'inda) okur.
 *        Firebase okuma-yedegi Faz 4'te kaldirildi.
 * YAZMA: create/update/delete + firma-bilgisi DAIMA dogrudan SQL'e yazar.
 *
 * KRITIK: subenin kanonik kimligi Firebase RTDB key'idir (= SQL Branch.FirebaseId).
 *         Tum sistem (personnel.branchesId, screen.branchId, ...) bu id'yi referans alir.
 *         Bu yuzden map'lerde `id = firebaseId` (SQL integer Id DEGIL).
 */

import { mainApi } from '../../config/apiClient';

export interface Branch {
  /** Kanonik sube kimligi (= Firebase RTDB key = SQL Branch.FirebaseId). */
  id: string;
  /** SQL integer primary key (yalnizca bilgi amacli — kanonik kimlik DEGIL). */
  sqlId?: number;
  name: string;
  address?: string;
  district?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  phone?: string;
  email?: string;
  website?: string;
  openingDate?: any;
  isHeadquarter?: boolean;
  isActive?: boolean;
  isFranchise?: boolean;
  // Franchise firma bilgisi (Firm*) — opsiyonel.
  firmName?: string;
  firmTaxNumber?: string;
  firmTaxOffice?: string;
  firmAddress?: string;
  firmPhone?: string;
  firmEmail?: string;
  firmWebsite?: string;
  firmMersisNo?: string;
  firmTradeRegistryNo?: string;
  /** Firma bilgisi alt-nesnesi (Firebase uyumu icin sentezlenir / spread edilir). */
  companyInfo?: Record<string, any>;
  // Firebase'den gelen ek alanlarin korunmasi icin passthrough.
  [key: string]: any;
}

export interface BranchWriteInput {
  name?: string;
  address?: string;
  district?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  phone?: string;
  email?: string;
  website?: string;
  openingDate?: string | Date | null;
  isFranchise?: boolean;
}

export interface BranchFirmInfo {
  firmName?: string;
  firmTaxNumber?: string;
  firmTaxOffice?: string;
  firmAddress?: string;
  firmPhone?: string;
  firmEmail?: string;
  firmWebsite?: string;
  firmMersisNo?: string;
  firmTradeRegistryNo?: string;
  isFranchise?: boolean;
}

function mapSqlBranch(b: any): Branch {
  // Firma bilgisi alt-nesnesini Firm* kolonlarindan sentezle (Firebase companyInfo uyumu).
  const companyInfo: Record<string, any> = {
    firmName: b?.firmName ?? undefined,
    firmTaxNumber: b?.firmTaxNumber ?? undefined,
    firmTaxOffice: b?.firmTaxOffice ?? undefined,
    firmAddress: b?.firmAddress ?? undefined,
    firmPhone: b?.firmPhone ?? undefined,
    firmEmail: b?.firmEmail ?? undefined,
    firmWebsite: b?.firmWebsite ?? undefined,
    firmMersisNo: b?.firmMersisNo ?? undefined,
    firmTradeRegistryNo: b?.firmTradeRegistryNo ?? undefined,
  };
  return {
    ...b,
    // KRITIK: kanonik id = firebaseId (SQL integer Id DEGIL).
    id: String(b?.firebaseId ?? b?.id ?? ''),
    sqlId: typeof b?.id === 'number' ? b.id : undefined,
    name: b?.name || 'İsimsiz Şube',
    // SQL'de isHeadquarter kolonu yok → false. HybridFallback fallback'inde Firebase'den gelir.
    isHeadquarter: false,
    isActive: b?.isActive ?? true,
    isFranchise: b?.isFranchise ?? false,
    companyInfo,
  };
}

/**
 * Sirketin subelerini getirir (SADECE SQL — GET /api/Branch).
 * NOT: "Subeye Ait Degil" gibi UI yardimci ogeleri BURADA EKLENMEZ (cagiran taraf ekler) —
 *      boylece firebaseDataService.getBranches reroute'unda vardiya/gorev listeleri bozulmaz.
 */
export async function getBranches(_companyId: string): Promise<Branch[]> {
  const res = await mainApi.get('/api/Branch');
  const raw = Array.isArray(res.data?.branches) ? res.data.branches : [];
  return raw.map(mapSqlBranch);
}

/**
 * Tek bir subeyi kanonik id (firebaseId) ile getirir (SADECE SQL).
 * Eski `getCompanyData(companyId, 'branches/{id}')` karsiligi.
 */
export async function getBranchById(companyId: string, branchId: string): Promise<Branch | null> {
  const branches = await getBranches(companyId);
  return branches.find((b) => b.id === branchId) ?? null;
}

function toIsoOrNull(d: string | Date | null | undefined): string | null {
  if (!d) return null;
  if (d instanceof Date) return d.toISOString();
  return String(d);
}

/** Yeni sube olusturur (SADECE SQL). Donus: yeni sube id (firebaseId). */
export async function createBranch(data: BranchWriteInput & { name: string }): Promise<string> {
  const res = await mainApi.post('/api/Branch/create', {
    name: data.name,
    address: data.address,
    district: data.district,
    city: data.city,
    postalCode: data.postalCode,
    country: data.country,
    phone: data.phone,
    email: data.email,
    website: data.website,
    openingDate: toIsoOrNull(data.openingDate),
    isFranchise: data.isFranchise,
  });
  const branchId = res?.data?.branchId ?? res?.data?.firebaseId;
  if (!branchId) throw new Error('createBranch: sunucu branchId dondurmedi');
  return String(branchId);
}

/** Sube genel bilgilerini gunceller (SADECE SQL). Firm* alanlarina dokunmaz. */
export async function updateBranch(branchId: string, data: BranchWriteInput): Promise<void> {
  await mainApi.post('/api/Branch/update', {
    firebaseId: branchId,
    name: data.name,
    address: data.address,
    district: data.district,
    city: data.city,
    postalCode: data.postalCode,
    country: data.country,
    phone: data.phone,
    email: data.email,
    website: data.website,
    openingDate: toIsoOrNull(data.openingDate),
    isFranchise: data.isFranchise,
  });
}

/** Sube siler (SADECE SQL — varsayilan soft delete). */
export async function deleteBranch(branchId: string, hardDelete = false): Promise<void> {
  await mainApi.post('/api/Branch/delete', { firebaseId: branchId, hardDelete });
}

/**
 * Sube franchise/firma bilgisini getirir (SADECE SQL).
 * Firma alani Branch satirinin Firm* kolonlarindan gelir.
 */
export async function getBranchFirmInfo(companyId: string, branchId: string): Promise<BranchFirmInfo | null> {
  const branches = await getBranches(companyId);
  const found = branches.find((b) => b.id === branchId);
  if (!found) return null;
  return {
    firmName: found.firmName,
    firmTaxNumber: found.firmTaxNumber,
    firmTaxOffice: found.firmTaxOffice,
    firmAddress: found.firmAddress,
    firmPhone: found.firmPhone,
    firmEmail: found.firmEmail,
    firmWebsite: found.firmWebsite,
    firmMersisNo: found.firmMersisNo,
    firmTradeRegistryNo: found.firmTradeRegistryNo,
    isFranchise: found.isFranchise,
  };
}

/** Sube firma bilgisini gunceller (SADECE SQL — POST /api/branch/update-firm-details). companyId body'de zorunlu. */
export async function updateBranchFirmInfo(
  companyId: string,
  branchId: string,
  firm: BranchFirmInfo
): Promise<void> {
  await mainApi.post('/api/branch/update-firm-details', {
    companyId,
    branchFirebaseId: branchId,
    firmName: firm.firmName,
    firmTaxNumber: firm.firmTaxNumber,
    firmTaxOffice: firm.firmTaxOffice,
    firmAddress: firm.firmAddress,
    firmPhone: firm.firmPhone,
    firmEmail: firm.firmEmail,
    firmWebsite: firm.firmWebsite,
    firmMersisNo: firm.firmMersisNo,
    firmTradeRegistryNo: firm.firmTradeRegistryNo,
  });
}
